import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Sparkles, Copy, Check } from 'lucide-react';
import { motion } from 'framer-motion';

const AIDailyReportCard = ({ reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const animationDuration = reducedMotion ? 0 : 0.5;

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/insights/daily`);
      const result = await response.json();
      setReport(result);
    } catch (error) {
      console.error('Error fetching daily report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (report) {
      const text = `${report.summary}\n\nSuggested Experiments:\n${report.suggested_experiments.map((e, i) => `${i+1}. ${e.title}: ${e.description}`).join('\n')}`;
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              AI Daily Report
            </CardTitle>
            <p className="text-xs text-slate-400 mt-1">
              Natural language insights and recommended experiments
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleCopy}
            className="text-xs"
          >
            {copied ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-pulse text-slate-400">Generating AI insights...</div>
          </div>
        ) : (
          <motion.div
            initial={reducedMotion ? {} : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: animationDuration }}
          >
            {/* Summary */}
            <div className="prose prose-invert prose-sm max-w-none mb-6">
              <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-lg p-6 border border-yellow-500/20">
                <pre className="whitespace-pre-wrap font-sans text-sm text-slate-200 leading-relaxed">
                  {report?.summary}
                </pre>
              </div>
            </div>

            {/* Suggested Experiments */}
            {report?.suggested_experiments && report.suggested_experiments.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-yellow-400" />
                  Suggested Experiments
                </h4>
                <div className="space-y-3">
                  {report.suggested_experiments.map((exp, index) => (
                    <motion.div
                      key={index}
                      className="bg-slate-800/50 rounded-lg p-4 border border-slate-700"
                      initial={reducedMotion ? {} : { opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="text-sm font-semibold text-slate-100">{exp.title}</h5>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            exp.confidence === 'high'
                              ? 'bg-emerald-500/20 text-emerald-400'
                              : exp.confidence === 'medium'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-slate-500/20 text-slate-400'
                          }`}
                        >
                          {exp.confidence} confidence
                        </span>
                      </div>
                      <p className="text-xs text-slate-300 mb-2">{exp.description}</p>
                      <p className="text-xs text-gold font-medium">
                        Expected: {exp.expected_impact}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Metadata */}
            {report && (
              <div className="mt-6 pt-6 border-t border-slate-700 text-center">
                <p className="text-xs text-slate-500">
                  Data age: {report.data_age} • Sample: {report.sample_size} calls • 
                  Updated: {new Date(report.updated_at).toLocaleString()}
                </p>
              </div>
            )}
          </motion.div>
        )}
      </CardContent>
    </Card>
  );
};

export default AIDailyReportCard;
